import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { ProvidersFilterOptions } from "@app/projects/network-assessment/maps/network-coverage-map/enumerators";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";

export enum ZipCoverageMapMembershipType {
  Primary = "Primary",
  Secondary = "Secondary",
}

/** A published run configuration offered in the "Results As Of" (Adequacy Time Slice) select. */
export interface ZipCoverageResultsAsOfOption {
  /** The run configuration id used as the option value. */
  id: number;
  /** The formatted date-created label shown to the user. */
  label: string;
}

/** A Service Area selection resolved to a single zip. countyRegionSetId = parent county's region-set id (accepted by the border endpoints). */
export interface ZipCoverageSelectedServiceArea {
  zipCode: string;
  countyRegionSetId: number;
  label: string;
  /** Parent county display name (e.g. "Jackson"), surfaced in the map details-bar callout. */
  countyName?: string;
  /** Parent state display name (e.g. "Missouri"), surfaced in the map details-bar callout. */
  stateName?: string;
}

/** Applied map-setup selections captured on Create. */
export interface ZipCoverageMapSelections {
  resultsAsOfId: number;
  membershipType: ZipCoverageMapMembershipType;
  serviceArea: ZipCoverageSelectedServiceArea;
  /** Every selected zip (ServiceAreaWideMappingZip). Absent or a single entry means the single-zip map. */
  serviceAreas?: ZipCoverageSelectedServiceArea[];
  specialtyGroupCode: DropdownItem<string>;
  providerSources: ProviderNetworkTypeEnum[];
}

export interface ZipProviderFilters {
  readonly providerFilterOptions: ProvidersFilterOptions[];
  readonly showExcludedProviders: boolean;
  readonly servesRange: [number, number];
  readonly gainsRange: [number, number];
  readonly impactRange: [number, number];
}

// Immutable snapshot rendered by the details bar. null renders as "---".
export interface ZipCoverageMapDetails {
  // Framing callout
  readonly zipCode: string;
  readonly zipName: string | null;
  readonly zipClass: string | null;
  readonly primaryCounty: string | null;
  readonly state: string | null;
  readonly specialtyGroupName: string;
  readonly passPercentage: number | null;
  readonly minimumProviderCount: number | null;
  readonly entityType: string | null;
  readonly minimumDistancePhrase: string | null;

  // Member metrics
  readonly totalMembers: number | null;
  readonly membersWithAccess: number | null;
  readonly membersWithAccessPercent: number | null;
  readonly membersWithoutAccess: number | null;
  readonly membersWithoutAccessPercent: number | null;

  // Distance / time metrics (null when the calc method excludes them or the feed is absent)
  readonly averageDistance: number | null;
  readonly maxDistance: number | null;
  readonly averageTime: number | null;
  readonly maxTime: number | null;

  // Provider counts
  readonly servicingProviderCount: number | null;
  readonly inZipServicingProviderCount: number | null;
}

/** A zip code selected in a run configuration's service area, as the Service Area list needs it. */
export interface ServiceAreaZip {
  readonly isZipPoint: boolean;
  readonly name: string;
  /** Decides which county the zip is listed under. */
  readonly primaryCountySSA: string;
  readonly zipCode: string;
}
